import { LineConnectorAnnotation } from "@/utils/types";
import { useActiveCanvas } from "@/utils/utils";
import React, { Fragment, useEffect, useMemo } from "react";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "./utils";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { Canvas } from "@/utils/types";

// These just support canvaswidth/canvasheight for the old canvas, remove after port to v2
const Annotations = React.memo(
  ({
    canvasWidth = 0,
    canvasHeight = 0,
  }: {
    canvasWidth?: number;
    canvasHeight?: number;
  }) => {
    const activeCanvas = useActiveCanvas();

    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

    const updateCanvas = useIssuesStore((state) => state.updateCanvas);

    const { lineAnnotations, annotationIdsToDelete } = useMemo(() => {
      const annotationIdsToDelete = [];

      const lineAnnotations = activeCanvas?.annotations
        ?.filter((ann) => {
          return ann.type === "LINE_CONNECTOR";
        })
        .map((ann: LineConnectorAnnotation) => {
          const fromStoryboard = activeCanvas.storyboards?.find(
            (sb) => sb.id === ann.fromStoryboardId,
          );
          const toStoryboard = activeCanvas.storyboards?.find(
            (sb) => sb.id === ann.toStoryboardId,
          );

          if (
            !fromStoryboard ||
            !toStoryboard ||
            fromStoryboard.deleted ||
            toStoryboard.deleted
          ) {
            // Delete annotation
            // @ts-expect-error TS2345
            annotationIdsToDelete.push(ann.id);

            return {
              ...ann,
              segments: [],
            };
          }

          const getPositionForStoryboard = (
            sb: any,
            position: "TOP" | "BOTTOM" | "LEFT" | "RIGHT",
          ) => {
            switch (position) {
              case "TOP":
                return { x: sb.x + sb.width / 2, y: sb.y };
              case "BOTTOM":
                return { x: sb.x + sb.width / 2, y: sb.y + sb.height };
              case "LEFT":
                return { x: sb.x, y: sb.y + sb.height / 2 };
              case "RIGHT":
                return { x: sb.x + sb.width, y: sb.y + sb.height / 2 };
            }
          };

          const fromPosition = getPositionForStoryboard(
            fromStoryboard,
            ann.fromPosition,
          );
          const toPosition = getPositionForStoryboard(
            toStoryboard,
            ann.toPosition,
          );

          const fromPositionVertical =
            ann.fromPosition === "TOP" || ann.fromPosition === "BOTTOM";
          const toPositionVertical =
            ann.toPosition === "TOP" || ann.toPosition === "BOTTOM";

          const segments = [];
          if (fromPositionVertical === toPositionVertical) {
            // If the same then there are 3 segments
            // @ts-expect-error TS2345
            segments.push({
              fromX: fromPosition.x,
              fromY: fromPosition.y,

              // Go in the direction of the connection (half way there)
              ...(fromPositionVertical
                ? {
                    toX: fromPosition.x,
                    toY: (fromPosition.y + toPosition.y) / 2,
                  }
                : {
                    toX: (fromPosition.x + toPosition.x) / 2,
                    toY: fromPosition.y,
                  }),
            });

            // Opposite segment
            // @ts-expect-error TS2345
            segments.push({
              fromX: toPosition.x,
              fromY: toPosition.y,

              // Go in the direction of the connection (half way there)
              ...(toPositionVertical
                ? {
                    toX: toPosition.x,
                    toY: (toPosition.y + fromPosition.y) / 2,
                  }
                : {
                    toX: (toPosition.x + fromPosition.x) / 2,
                    toY: toPosition.y,
                  }),
            });

            // Finally conect the two segments
            // @ts-expect-error TS2345
            segments.push({
              // @ts-expect-error TS2339
              fromX: segments[0].toX,
              // @ts-expect-error TS2339
              fromY: segments[0].toY,
              // @ts-expect-error TS2339
              toX: segments[1].toX,
              // @ts-expect-error TS2339
              toY: segments[1].toY,
            });
          } else {
            // If different there are only 2 segments as the direction changes
            // @ts-expect-error TS2345
            segments.push({
              fromX: fromPosition.x,
              fromY: fromPosition.y,

              // Go in the direction of the connection
              ...(fromPositionVertical
                ? {
                    toX: fromPosition.x,
                    toY: toPosition.y,
                  }
                : {
                    toX: toPosition.x,
                    toY: fromPosition.y,
                  }),
            });

            // @ts-expect-error TS2345
            segments.push({
              fromX: toPosition.x,
              fromY: toPosition.y,
              // @ts-expect-error TS2339
              toX: segments[0].x,
              // @ts-expect-error TS2339
              toY: segments[0].y,
            });
          }

          return {
            ...ann,
            segments,
          };
        });

      return { lineAnnotations, annotationIdsToDelete };
    }, [activeCanvas?.annotations, activeCanvas?.storyboards]);

    useEffect(() => {
      const deleteAnnotations = async () => {
        if (!supabaseClient) {
          return;
        }

        if (activeCanvas?.id && annotationIdsToDelete.length > 0) {
          const newAnnotations = activeCanvas.annotations.filter((ann) => {
            // @ts-expect-error TS2345
            return !annotationIdsToDelete.includes(ann.id);
          });

          await supabaseClient
            .from("canvases")
            .update({ annotations: newAnnotations })
            .eq("id", activeCanvas.id);
        }
      };

      deleteAnnotations();
    }, [annotationIdsToDelete, supabaseClient]);

    return (
      <>
        {lineAnnotations?.map((annotation) => {
          return (
            <Fragment key={annotation.id}>
              {annotation?.segments?.map?.((segment, index) => {
                const left =
                  // @ts-expect-error TS2339
                  canvasWidth / 2 + Math.min(segment.fromX, segment.toX) - 2;
                const top =
                  // @ts-expect-error TS2339
                  canvasHeight / 2 + Math.min(segment.fromY, segment.toY) - 2;
                const right =
                  // @ts-expect-error TS2339
                  canvasWidth / 2 + Math.max(segment.fromX, segment.toX) + 2;
                const bottom =
                  // @ts-expect-error TS2339
                  canvasHeight / 2 + Math.max(segment.fromY, segment.toY) + 2;

                // Middle of the canvas is 0, 0
                return (
                  <div
                    key={index}
                    className="absolute bg-secondary-n-600"
                    style={{
                      transform: `translate3d(${left}px, ${top}px, 0)`,
                      transformOrigin: "0 0",
                      width: right - left,
                      height: bottom - top,
                    }}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </>
    );
  },
);
Annotations.displayName = "Annotations";

export default Annotations;
